import React, { useEffect } from 'react';

import { getConfig } from '@edx/frontend-platform';
import { AppProvider } from '@edx/frontend-platform/react';
import { Helmet } from 'react-helmet';
import { Navigate, Route, Routes } from 'react-router-dom';
import Header from '@edx/frontend-component-header';
import FooterSlot from '@openedx/frontend-slot-footer';

import {
  EmbeddedRegistrationRoute, NotFoundPage, registerIcons, UnAuthOnlyRoute, Zendesk,
} from './common-components';
import configureStore from './data/configureStore';
import {
  AUTHN_PROGRESSIVE_PROFILING,
  LOGIN_PAGE,
  PAGE_NOT_FOUND,
  PASSWORD_RESET_CONFIRM,
  RECOMMENDATIONS,
  REGISTER_EMBEDDED_PAGE,
  REGISTER_PAGE,
  RESET_PAGE,
} from './data/constants';
import { updatePathWithQueryParams } from './data/utils';
import { ForgotPasswordPage } from './forgot-password';
import Logistration from './logistration/Logistration';
import { ProgressiveProfiling } from './progressive-profiling';
import { RecommendationsPage } from './recommendations';
import { RegistrationPage } from './register';
import { ResetPasswordPage } from './reset-password';

import './index.scss';

registerIcons();

const MainApp = () => {
  useEffect(() => {
    const lockAttr = 'data-authn-scroll-lock';
    const lockToken = 'authn-modal-lock-v2';
    const blockedKeys = new Set([' ', 'PageUp', 'PageDown', 'End', 'Home', 'ArrowUp', 'ArrowDown']);
    let scrollSnapshot = null;

    const isTypingTarget = (target) => {
      if (!(target instanceof HTMLElement)) {
        return false;
      }
      const tag = target.tagName.toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable;
    };

    const hasOpenModal = () => Boolean(
      document.querySelector(
        '#myModal.show, .modal.show, .modal-backdrop.show, .pgn__modal-backdrop, [role="dialog"][aria-modal="true"]',
      ),
    );

    const lockScroll = () => {
      const html = document.documentElement;
      const body = document.body;
      if (body.getAttribute(lockAttr) === lockToken) {
        return;
      }

      const scrollY = window.pageYOffset || html.scrollTop || 0;
      scrollSnapshot = {
        scrollY,
        htmlOverflow: html.style.overflow,
        bodyOverflow: body.style.overflow,
        bodyPosition: body.style.position,
        bodyTop: body.style.top,
        bodyLeft: body.style.left,
        bodyRight: body.style.right,
        bodyWidth: body.style.width,
      };

      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.setAttribute(lockAttr, lockToken);
    };

    const unlockScroll = () => {
      const html = document.documentElement;
      const body = document.body;
      if (body.getAttribute(lockAttr) !== lockToken || !scrollSnapshot) {
        return;
      }

      html.style.overflow = scrollSnapshot.htmlOverflow;
      body.style.overflow = scrollSnapshot.bodyOverflow;
      body.style.position = scrollSnapshot.bodyPosition;
      body.style.top = scrollSnapshot.bodyTop;
      body.style.left = scrollSnapshot.bodyLeft;
      body.style.right = scrollSnapshot.bodyRight;
      body.style.width = scrollSnapshot.bodyWidth;
      body.removeAttribute(lockAttr);
      window.scrollTo(0, scrollSnapshot.scrollY);
      scrollSnapshot = null;
    };

    const syncLockState = () => {
      if (hasOpenModal()) {
        lockScroll();
      } else {
        unlockScroll();
      }
    };

    const blockScroll = (event) => {
      if (document.body.getAttribute(lockAttr) === lockToken) {
        event.preventDefault();
      }
    };

    const blockKeyScroll = (event) => {
      if (document.body.getAttribute(lockAttr) !== lockToken) {
        return;
      }
      if (blockedKeys.has(event.key) && !isTypingTarget(event.target)) {
        event.preventDefault();
      }
    };

    const observer = new MutationObserver(syncLockState);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden'],
    });

    document.addEventListener('shown.bs.modal', syncLockState);
    document.addEventListener('hidden.bs.modal', syncLockState);
    window.addEventListener('wheel', blockScroll, { passive: false, capture: true });
    window.addEventListener('touchmove', blockScroll, { passive: false, capture: true });
    window.addEventListener('keydown', blockKeyScroll, { passive: false, capture: true });
    syncLockState();

    return () => {
      observer.disconnect();
      document.removeEventListener('shown.bs.modal', syncLockState);
      document.removeEventListener('hidden.bs.modal', syncLockState);
      window.removeEventListener('wheel', blockScroll, true);
      window.removeEventListener('touchmove', blockScroll, true);
      window.removeEventListener('keydown', blockKeyScroll, true);
      unlockScroll();
    };
  }, []);

  return (
    <AppProvider store={configureStore()}>
      <Helmet>
        <link rel="shortcut icon" href={getConfig().FAVICON_URL} type="image/x-icon" />
      </Helmet>
      {getConfig().ZENDESK_KEY && <Zendesk />}
      <div className="d-flex flex-column" style={{ minHeight: '100vh' }}>
        <Header />
        <main className="flex-grow-1" id="main">
          <Routes>
            <Route path="/" element={<Navigate replace to={updatePathWithQueryParams(REGISTER_PAGE)} />} />
            <Route
              path={REGISTER_EMBEDDED_PAGE}
              element={<EmbeddedRegistrationRoute><RegistrationPage /></EmbeddedRegistrationRoute>}
            />
            <Route
              path={LOGIN_PAGE}
              element={
                <UnAuthOnlyRoute><Logistration selectedPage={LOGIN_PAGE} /></UnAuthOnlyRoute>
              }
            />
            <Route path={REGISTER_PAGE} element={<UnAuthOnlyRoute><Logistration /></UnAuthOnlyRoute>} />
            <Route path={RESET_PAGE} element={<UnAuthOnlyRoute><ForgotPasswordPage /></UnAuthOnlyRoute>} />
            <Route path={PASSWORD_RESET_CONFIRM} element={<ResetPasswordPage />} />
            <Route path={AUTHN_PROGRESSIVE_PROFILING} element={<ProgressiveProfiling />} />
            <Route path={RECOMMENDATIONS} element={<RecommendationsPage />} />
            <Route path={PAGE_NOT_FOUND} element={<NotFoundPage />} />
            <Route path="*" element={<Navigate replace to={PAGE_NOT_FOUND} />} />
          </Routes>
        </main>
        <FooterSlot />
      </div>
    </AppProvider>
  );
};

export default MainApp;
